import React, { Fragment, type ReactElement } from "react";
import { ExternalLink } from "lucide-react";
import { RD_STRANDED_ROW_HINT } from "../../../../shared/rd-registry-copy";
import {
  signInNameDiffers,
  type RegistrySignInAccount,
} from "../../lib/rd-registry-lanes";

export interface RdIdentityLineProps {
  rdName: string;
  signInAccount: RegistrySignInAccount | null;
  healthieRecordUrl: string | null;
}

const MUTED_CLASS = "text-muted-foreground";

function Gap({ children }: { children: string }): ReactElement {
  return <span className="text-warning">{children}</span>;
}

function Muted({ children }: { children: string }): ReactElement {
  return <span className={MUTED_CLASS}>{children}</span>;
}

function HealthieRecordLink({ url }: { url: string }): ReactElement {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-0.5 text-primary hover:underline"
    >
      Healthie
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

function accountParts(
  rdName: string,
  account: RegistrySignInAccount | null
): ReactElement[] {
  if (!account) return [<Gap key="account-gap">No sign-in account</Gap>];

  const email = <Muted key="email">{account.email}</Muted>;
  if (!signInNameDiffers(account.name, rdName)) return [email];

  return [<Muted key="account-name">{account.name}</Muted>, email];
}

function healthiePart(url: string | null): ReactElement {
  if (!url) return <Gap key="healthie-gap">No Healthie record</Gap>;

  return <HealthieRecordLink key="healthie" url={url} />;
}

function hint(text: string): ReactElement {
  return <Muted key="hint">{text}</Muted>;
}

function identityParts({
  rdName,
  signInAccount,
  healthieRecordUrl,
}: RdIdentityLineProps): ReactElement[] {
  const account = accountParts(rdName, signInAccount);
  const healthie = healthiePart(healthieRecordUrl);

  if (!signInAccount && healthieRecordUrl) {
    return [...account, hint("link one in NDD Admin"), healthie];
  }

  if (!signInAccount) return [...account, healthie, hint(RD_STRANDED_ROW_HINT)];

  if (!healthieRecordUrl) {
    return [...account, healthie, hint("add them in Healthie first")];
  }

  return [...account, healthie];
}

/**
 * One line under the RD's name naming what is and is not linked. Gaps are
 * amber text, never pills, and the line holds no control: links are made in
 * NDD Admin, not here.
 */
export function RdIdentityLine(props: RdIdentityLineProps): ReactElement {
  const parts = identityParts(props);

  return (
    <p className="mt-0.5 text-label text-muted-foreground">
      {parts.map((part, index) => (
        <Fragment key={part.key}>
          {index > 0 && " · "}
          {part}
        </Fragment>
      ))}
    </p>
  );
}
